import Login from 'apps/company/pages/auth/Login';
import ForgotPassword from 'apps/company/pages/auth/ForgotPassword';
import ResetPassword from 'apps/company/pages/auth/ResetPassword';
import React from 'react';
import {
  BrowserRouter,
  Route,
  Routes as Router,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router-dom';
import ProtectedRoutes from './ProtectedRoutes';
import DefaultRoutes from './DefaultRoutes';
import RouteName from 'config/routes';
import NotFound from 'components/NotFound';
import { Loading } from 'components';
import * as Sentry from '@sentry/react';
import Maintenance from 'components/Maintenance';

const UserList = React.lazy(() => import('apps/company/pages/User/List'));
const UserDetail = React.lazy(() => import('apps/company/pages/User/Detail'));
const UserAdd = React.lazy(() => import('apps/company/pages/User/Add'));

const TrainingReportList = React.lazy(() => import('apps/company/pages/TrainingReportConfirm/List'));
const TrainingReportDetail = React.lazy(() => import('apps/company/pages/TrainingReportConfirm/Detail'));

const TrainingCategoryList = React.lazy(() => import('apps/company/pages/TrainingCategory/List'));
const TrainingList = React.lazy(() => import('apps/company/pages/Training/List'));
const TrainingDetail = React.lazy(() => import('apps/company/pages/Training/Detail'));

const CompanyInfo = React.lazy(() => import('apps/company/pages/CompanyInfo'));
const CompanyNotification = React.lazy(() => import('apps/company/pages/CompanyNotification'));

const CompanyUserList = React.lazy(() => import('apps/company/pages/CompanyUser/List'));
const CompanyUserDetail = React.lazy(() => import('apps/company/pages/CompanyUser/Detail/'));
const CompanyUserAdd = React.lazy(() => import('apps/company/pages/CompanyUser/Add'));
const CompanyUserEdit = React.lazy(() => import('apps/company/pages/CompanyUser/Add'));

const AddBranch = React.lazy(() => import('apps/company/pages/Branch/Add'))
const RequiredTrainingList = React.lazy(() => import('apps/company/pages/RequiredTraining/List'));
const RequiredTrainingListUser = React.lazy(() => import('apps/company/pages/RequiredTraining/ListUser'));

const SystemNotification = React.lazy(() => import('apps/company/pages/SystemNotification'));
const Terms = React.lazy(() => import('apps/company/pages/Terms'));

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  environment: process.env.NODE_ENV,
  tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_SAMPLE_RATE),
  maxBreadcrumbs: 30,
});

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Router);

// Fix chunk load error when production deploy new version
window.addEventListener('error', (e) => {
  if (e?.message?.includes('Loading chunk') || e?.message?.includes('ChunkLoadError')) {
    console.warn('ChunkLoadError detected. Reloading page with cache bypass...');
    window.location.reload(true);
  }
});

const Routes = () => (
  <BrowserRouter basename="/admin">
    <SentryRoutes>
      <Route path="/maintenance" element={<Maintenance returnTo = '/admin'/>} />
      <Route path="/" element={<ProtectedRoutes />}>
        <Route
          path={RouteName.trainingReport}
          exact
          index
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <TrainingReportList />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.userList}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <UserList />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.userDetail}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <UserDetail />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.userAdd}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <UserAdd />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.trainingReportCompany}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <TrainingReportList />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.userEdit}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <UserAdd />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.companyInfo}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <CompanyInfo />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.companyNotification}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <CompanyNotification />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.branchAdd}
          exact
          index
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <AddBranch />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.branchEdit}
          exact
          index
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <AddBranch />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.companyUserList}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <CompanyUserList />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.companyUserDetail}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <CompanyUserDetail />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.companyUserAdd}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <CompanyUserAdd />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.companyUserEdit}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <CompanyUserEdit />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.trainingReportDetail}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <TrainingReportDetail />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.trainingCategoryList}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <TrainingCategoryList />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.trainingList}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <TrainingList />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.trainingDetail}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <TrainingDetail />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.requiredTraining}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <RequiredTrainingList />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.requiredTrainingListUser}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <RequiredTrainingListUser />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.systemNotification}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <SystemNotification />
            </React.Suspense>
          }
        />
        <Route path="*" element={
          <React.Suspense fallback="loading">
            <NotFound />
          </React.Suspense>
        }
        />
      </Route>
      <Route element={<DefaultRoutes />}>
        <Route
          path={RouteName.loginCompany}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <Login />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.forgotPassword}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <ForgotPassword />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.resetPassword}
          element={
            <React.Suspense
              fallback={<Loading isLoading={true} />}
            >
              <ResetPassword />
            </React.Suspense>
          }
        />
        <Route
          path={RouteName.notFound}
          element={
            <React.Suspense fallback="loading">
              <NotFound />
            </React.Suspense>
          }
        />
        <Route path={RouteName.termOfUse}
          element={
            <React.Suspense
              fallback={
                <Loading isLoading={true} />
              }
            >
              <Terms />
            </React.Suspense>
          }
        />
      </Route>
    </SentryRoutes>
  </BrowserRouter>
);

export default Routes;
